<template>
  <transition name="fade">
    <div v-if="visible" class="fixed" @click="closePopup">
      <div class="modal-card" @click.stop>
        <div class="modal-header">
          <h2 class="title">Añadir nueva fecha</h2>
          <button type="button" class="close-btn" @click="closePopup">&times;</button>
        </div>

        <div v-if="props.product" class="product-info-summary">
          <h3 class="product-title">{{ props.product.titulo }}</h3>
          <div class="info-row">
            <span class="info-label">Fecha actual:</span>
            <span class="info-value" v-if="props.product.fecha">
              {{ formatDate(props.product.fecha) }}
              <template v-if="props.product.hora"> a las {{ props.product.hora }}</template>
            </span>
            <span class="info-value" v-else>Sin fecha</span>
          </div>
          <div class="info-row">
            <span class="info-label">Plazas actuales:</span>
            <span class="info-value">{{ props.product.stock_maximo }}</span>
          </div>
        </div>

        <form @submit.prevent="cloneProduct" class="form">
          <div class="form-row">
            <div class="form-group col">
              <label for="new-date">Nueva fecha <span class="required">*</span></label>
              <input
                id="new-date"
                v-model="newDate"
                type="date"
                required
              />
            </div>

            <div class="form-group col">
              <label for="new-hour">Hora <span class="required">*</span></label>
              <input
                id="new-hour"
                v-model="newHour"
                type="time"
                required
              />
            </div>
          </div>

          <div class="form-group">
            <label for="new-stock">Nuevas plazas máximas <span class="required">*</span></label>
            <input
              id="new-stock"
              v-model.number="newStock"
              type="number"
              min="1"
              required
            />
          </div>

          <div class="buttons-container">
            <button type="button" class="secondary_button w-full" @click="closePopup">Cancelar</button>
            <button type="submit" class="main_button-green w-full">Añadir fecha</button>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue';
import { format, parse } from 'date-fns';
import { es } from 'date-fns/locale';
import { useLoadingStore } from "@/stores/loadingStore";
import { refreshToken } from "@/services/authService";
import { useRouter } from 'vue-router';

const router = useRouter();
const loadingStore = useLoadingStore();

const props = defineProps({
  product: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close-add-date', 'saved-add-date', 'error-add-date']);

const visible = ref(false);
const newDate = ref('');
const newHour = ref('');
const newStock = ref(1);

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const fechaAux = parse(dateStr, "yyyy-MM-dd", new Date());
    return format(fechaAux, "dd MMM yyyy", { locale: es });
  } catch (e) {
    return dateStr;
  }
};

watch(
  () => props.product,
  (newProd) => {
    if (newProd) {
      visible.value = true;
      newDate.value = newProd.fecha || '';
      newHour.value = newProd.hora || '';
      newStock.value = newProd.stock_maximo || 1;
    } else {
      visible.value = false;
    }
  },
  { immediate: true }
);

const closePopup = () => {
  visible.value = false;
  emit('close-add-date');
};

const cloneProduct = async () => {
  if (!newDate.value || !newStock.value) {
    emit('error-add-date', 'Por favor, completa los campos requeridos.');
    return;
  }

  loadingStore.setLoading(true);
  try {
    let response = await cloneProductAPI();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await cloneProductAPI();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      loadingStore.setLoading(false);
      emit('error-add-date', "Se ha producido un error clonando el producto.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      loadingStore.setLoading(false);
      emit('error-add-date', data.message);
    } else {
      loadingStore.setLoading(false);
      emit('saved-add-date', props.product.id_promo);
      closePopup();
    }
  } catch (exception) {
    loadingStore.setLoading(false);
    emit('error-add-date', exception.message);
  }
};

const formatDateToYYYYMMDD = (val) => {
  if (!val) return '';
  if (val instanceof Date) {
    return format(val, 'yyyy-MM-dd');
  }
  const str = String(val).trim();
  const datePart = str.split('T')[0].split(' ')[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }
  try {
    const parsed = new Date(str);
    if (!isNaN(parsed.getTime())) {
      return format(parsed, 'yyyy-MM-dd');
    }
  } catch (e) {
    // fallback
  }
  return datePart;
};

async function cloneProductAPI() {
  const formattedDate = formatDateToYYYYMMDD(newDate.value);

  return await fetch(process.env.VUE_APP_API_URL + "api/dashboard/plans/add_date", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    },
    body: JSON.stringify({
      "user_id": localStorage.getItem('user_id'),
      "product_id": props.product?.id_producto_promo,
      "date": formattedDate,
      "hour": newHour.value,
      "stock": newStock.value
    }),
  });
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 500;
  padding: 16px;
  box-sizing: border-box;
}

.modal-card {
  background-color: #ffffff;
  padding: 24px;
  border-radius: var(--radius-md);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--very-clear-gray, #eeeeee);
  padding-bottom: 12px;
  margin-bottom: 18px;
}

.title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-color-dark, #2f4858);
  margin: 0;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: var(--clear-gray, #607d94);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;
}

.close-btn:hover {
  color: var(--text-color-dark, #2f4858);
  background-color: var(--almost-white, #f5f5f5);
}

.product-info-summary {
  background-color: rgba(32, 205, 168, 0.06);
  border: 1px solid rgba(32, 205, 168, 0.3);
  border-radius: var(--radius-md);
  padding: 1rem;
  margin-bottom: 1.25rem;
  text-align: left;
}

.product-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-color-dark, #2f4858);
  margin: 0 0 0.5rem 0;
}

.info-row {
  font-size: 14px;
  color: var(--text-color-light, #607d94);
  margin-bottom: 0.25rem;
}

.info-label {
  font-weight: 600;
  margin-right: 0.5rem;
}

.info-value {
  color: var(--text-color-dark, #2f4858);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color-dark, #2f4858);
}

.required {
  color: #e53e3e;
}

.form-row {
  display: flex;
  gap: 12px;
}

.col {
  flex: 1;
}

.buttons-container {
  display: flex;
  gap: 12px;
  margin-top: 1rem;
}

.w-full {
  width: 100%;
}
</style>
